/**
 * Public booking links — the guest-facing "where do I book this unit" URLs.
 *
 * One definition shared by the two surfaces that touch them (tasks/property-public-booking-links.md):
 *   - the property EDIT form, where admin/owner pastes them in
 *   - the property DETAILS panel, where ops reads and copies them
 *
 * The OTA links are stored on the property (`property.publicLinks`). The direct-booking link is NOT
 * stored — it is derived from the property id, so it can never drift out of date with the route.
 */

/** The platforms we hold a public listing link for, in display order. */
export const PUBLIC_LINK_PLATFORMS = [
  { key: 'airbnb', label: 'Airbnb', placeholder: 'https://www.airbnb.com/rooms/123456789' },
  { key: 'booking', label: 'Booking.com', placeholder: 'https://www.booking.com/hotel/sa/example.html' },
  { key: 'gathern', label: 'Gathern', placeholder: 'https://gathern.co/unit/123456' }
];

/**
 * The direct-booking page for a unit. Single definition — the public route is `/book/:slug` where
 * the slug is the property id (see AppSecure.js), and the `#` is required by HashRouter.
 */
export const getDirectBookingUrl = (propertyId) =>
  propertyId ? `${window.location.origin}/#/book/${propertyId}` : '';

/**
 * Mirrors the backend rule in `securePropertyRoutes.js` — https only. An OTA listing URL that is
 * not https is a typo, and a broken link sent to a guest costs a booking.
 */
export const isValidPublicLink = (url) => !url || /^https:\/\/\S+$/i.test(url.trim());

/**
 * Every bookable link for a property, ready to render: the OTA links that have been entered, plus
 * the direct-booking link when the feature is switched on for this unit.
 */
export const collectBookingLinks = (property) => {
  if (!property) return [];

  const links = PUBLIC_LINK_PLATFORMS
    .map(({ key, label }) => ({ key, label, url: (property.publicLinks?.[key] || '').trim() }))
    .filter((link) => link.url);

  if (property.features?.directBookingEnabled) {
    links.push({ key: 'direct', label: 'الحجز المباشر', url: getDirectBookingUrl(property._id) });
  }

  return links;
};

/**
 * The links as one block of text for a WhatsApp message — label above URL, blank line between, so
 * it stays readable when the recipient's client renders it RTL.
 */
export const formatBookingLinksForSharing = (links) =>
  links.map(({ label, url }) => `${label}\n${url}`).join('\n\n');
